import {PostsContext} from '@/contexts/PostsProvider';
import {ThemeContext} from '@/contexts/ThemeProvider';
import {VideoContext} from '@/contexts/VideoProvider';
import useErrorHandler from '@/hooks/useErrorHandler';
import useThemeColor from '@/hooks/useThemeColor';
import {Post, Query, QueryPostsArgs} from '@/types/generated-types';
import {gql, useQuery} from '@apollo/client';
import React, {useContext, useEffect, useRef, useState} from 'react';
import {FlatList, StyleSheet, Text, View} from 'react-native';
import {ActivityIndicator} from 'react-native-paper';
import PostItem from './PostItem';
import {Ionicons} from '@expo/vector-icons';
import StyledPrimaryButton from './Styled/StyledPrimaryButton';

const POSTS_QUERY = gql`
  query Posts($page: Int!) {
    posts(first: 10, page: $page) {
      paginatorInfo {
        count
        currentPage
        hasMorePages
      }
      data {
        id
        title
        subTitle
        images {
          url
          is_video
        }
        youtubeId
        publishedAt
        access
        isLiked
        likeCount
        commentCount
        questionCount
      }
    }
  }
`;

const PostsList = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showEmptyState, setShowEmptyState] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);
  const {handle: handleError} = useErrorHandler();
  const {loading, error, data, refetch} = useQuery<Query, QueryPostsArgs>(
    POSTS_QUERY,
    {
      fetchPolicy: 'network-only',
      variables: {page} as QueryPostsArgs,
    },
  );
  const {posts: postsData, setPosts: setPostsData} = useContext(PostsContext);

  const {setIndex} = useContext(VideoContext);

  function handleRefresh() {
    setPage(1);
    setShowEmptyState(false);
    setHasMorePages(false);
    setIsRefreshing(true);
    setPostsData([]);
    getAllPosts();
  }

  useEffect(() => {
    getAllPosts();
  }, [page]);

  useEffect(() => {
    setShowEmptyState(true);
  }, []);

  async function getAllPosts() {
    const newData = await refetch({page});
    if (page === 1) {
      setPostsData(newData.data?.posts?.data);
    } else {
      setPostsData([...postsData, ...(newData.data?.posts?.data || [])]);
    }

    setHasMorePages(newData.data?.posts?.paginatorInfo.hasMorePages || false);

    setIsRefreshing(false);

    if (postsData.length === 0) {
      setShowEmptyState(true);
    }
  }

  function handleEnd() {
    if (!loading && hasMorePages) {
      setPage(page + 1);
    }
  }

  useEffect(() => {
    if (error) {
      handleError(error);
      console.log('error', error);
    }
  }, [error]);

  const {isDark} = useContext(ThemeContext);

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      flexDirection: 'column',
      padding: 20,
      justifyContent: 'center',
      alignItems: 'center',
    },
    list: {
      flex: 1,
      flexDirection: 'column',
      flexGrow: 1,
      width: '100%',
      backgroundColor: useThemeColor('background'),
    },
    postSeparator: {
      borderBottomWidth: 1,
      borderBottomColor: isDark() ? '#222' : '#e5e7eb',
    },
    iconContainer: {
      padding: 10,
      borderRadius: 96,
      backgroundColor: useThemeColor('headerColor'),
      marginBottom: 16,
    },
    text: {
      color: useThemeColor('text'),
      textAlign: 'center',
    },
    loading: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
  });

  const onViewableItemsChanged = ({viewableItems}) => {
    if (viewableItems && viewableItems.length > 0) {
      setIndex(viewableItems[0].index);
    }
  };

  const viewabilityConfig = {
    viewAreaCoveragePercentThreshold: 90,
    waitForInteraction: true,
  };
  const viewabilityConfigCallbackPairs = useRef([
    {
      viewabilityConfig,
      onViewableItemsChanged,
    },
  ]);

  const iconColor = useThemeColor('background');

  const renderEmpty = () =>
    !loading && showEmptyState ? (
      <View style={styles.container}>
        <View style={styles.iconContainer}>
          <Ionicons name="newspaper" size={48} color={iconColor} />
        </View>
        <Text style={styles.text}>No hay publicaciones</Text>
        <StyledPrimaryButton
          onPress={() => handleRefresh()}
          text="Volver a cargar"
          style={{marginTop: 12}}
          loading={loading}
        />
      </View>
    ) : (
      <></>
    );

  // TODO: show good errors UI
  if (error) {
    return <Text>Error :(</Text>;
  }

  const renderItem = ({item: post, index}: {item: Post}) => (
    <PostItem index={index} post={post} />
  );

  return (
    <FlatList
      style={styles.list}
      snapToAlignment="start"
      disableIntervalMomentum
      showsVerticalScrollIndicator={false}
      renderItem={renderItem}
      keyExtractor={item => item.id}
      data={postsData}
      ItemSeparatorComponent={() => <View style={styles.postSeparator} />}
      refreshing={isRefreshing}
      ListEmptyComponent={renderEmpty}
      onRefresh={handleRefresh}
      onEndReached={handleEnd}
      onEndReachedThreshold={0.5}
      ListFooterComponent={() =>
        hasMorePages ? (
          <ActivityIndicator
            style={(styles.loading, {padding: 20})}
            size={24}
            color="gray"
          />
        ) : (
          <></>
        )
      }
      viewabilityConfigCallbackPairs={viewabilityConfigCallbackPairs.current}
    />
  );
};

export default PostsList;
